/**
 * La trazabilidad de una orden de pago: de qué contratación nació, qué pago la
 * cerró, qué suscripción quedó viva y qué certificados se emitieron.
 *
 * La cadena en la base es:
 *
 *   contrataciones ← ordenes_pago ─┬→ pagos ─────────→ certificados
 *                                  └→ subscriptions ─→ certificados
 *
 * Un certificado puede colgar del pago (`certificados.pago_id`) o de la
 * suscripción (`certificados.subscription_id`), y a veces de los dos; por eso se
 * juntan las dos ramas y se deduplica por id. Una suscripción emite uno o dos
 * certificados (titular y carga), así que `certificados` es siempre una lista.
 *
 * MULTIPRODUCTO: LA ORDEN ES EL ANCLA, NO LA CONTRATACIÓN
 * ------------------------------------------------------
 * Un carrito de N productos cuelga de UNA orden: N contrataciones comparten
 * orden, pago y suscripción. Pero `ordenes_pago.contratacion_id` guarda solo la
 * primaria, así que leer la cadena por ese campo muestra un contrato de los N —y
 * junto a él los certificados de todos, que es peor que faltar: se leía como si
 * ese certificado fuera de ese contrato. Las N salen del carrito
 * (`contrataciones.carrito_id`) y cada certificado se liga a la suya con
 * `subscriptions.contrataciones_data`.
 */

import { msDeDb } from '../fechas.js';

const lista = (valor) => (Array.isArray(valor) ? valor.filter(Boolean) : valor ? [valor] : []);

// Estados con los que un PSP declara que el dinero se capturó. Si hay varios
// pagos (reintentos) el que manda es el que cerró la orden, no el último.
const PAGO_EXITOSO = new Set(['authorized', 'completed', 'approved', 'success', 'paid']);

// `pagos.transaction_date` y `pagos.created_at` son `timestamp without time
// zone`: sin normalizar, `new Date()` los lee como hora local y los corre 3 o 4
// horas, mientras que `subscriptions.created_at` (timestamptz) queda en su
// instante real. Comparar unos con otros así desordena la cadena. `msDeDb` da
// `null` cuando no hay fecha, y el `?? 0` manda esa fila al final.
const masReciente = (a, b) => {
  const fecha = (p) => msDeDb(p?.transaction_date) ?? msDeDb(p?.created_at) ?? 0;
  return fecha(b) - fecha(a);
};

/** El pago que representa a la orden: el que resultó exitoso, si no el más reciente. */
export function pagoPrincipal(pagos) {
  const ordenados = lista(pagos).slice().sort(masReciente);
  return ordenados.find((p) => PAGO_EXITOSO.has(String(p?.status || '').toLowerCase())) || ordenados[0] || null;
}

/** La suscripción viva de la orden: la que no está cancelada, si no la más reciente. */
export function suscripcionPrincipal(suscripciones) {
  const ordenados = lista(suscripciones).slice().sort(masReciente);
  return ordenados.find((s) => String(s?.status || '').toLowerCase() !== 'cancelled') || ordenados[0] || null;
}

/** Certificados de las dos ramas (pago y suscripción), sin repetidos y por código. */
export function certificadosDeOrden(orden) {
  const porId = new Map();
  const juntar = (certs) =>
    lista(certs).forEach((c) => {
      if (c?.id && !porId.has(c.id)) porId.set(c.id, c);
    });

  lista(orden?.pagos).forEach((p) => juntar(p?.certificados));
  lista(orden?.subscriptions).forEach((s) => juntar(s?.certificados));

  return [...porId.values()].sort((a, b) =>
    String(a?.codigo ?? '').localeCompare(String(b?.codigo ?? ''), 'es', { numeric: true })
  );
}

/**
 * Las N contrataciones que cubre la orden, empezando por la primaria.
 *
 * Salen del carrito de la primaria. Si el embed del carrito no vino —o la orden es
 * de un solo producto— devuelve solo la primaria: una lista de uno, nunca vacía si
 * hay contratación. Se ordena por código para que el listado sea estable.
 */
export function contratacionesDeOrden(orden) {
  const primaria = orden?.contrataciones || null;
  if (!primaria) return [];

  const hermanas = lista(primaria?.carritos?.contrataciones);
  if (hermanas.length === 0) return [primaria];

  // La primaria viene con más campos que las del embed del carrito (nombre, mail,
  // rut), así que su versión manda; las demás se agregan tal como vienen.
  const porId = new Map([[primaria.id, primaria]]);
  hermanas.forEach((c) => {
    if (c?.id && !porId.has(c.id)) porId.set(c.id, c);
  });

  return [...porId.values()].sort((a, b) =>
    String(a?.codigo ?? '').localeCompare(String(b?.codigo ?? ''), 'es', { numeric: true })
  );
}

/**
 * De qué contratación es cada certificado: `{ [certificado_id]: contratacion_id }`.
 *
 * `certificados` no tiene columna `contratacion_id`; el enlace vive en
 * `subscriptions.contrataciones_data`, que el emisor escribe al emitir. Sin esto no
 * hay forma de saber qué certificado corresponde a qué contrato del carrito.
 *
 * Tolera la forma legacy de `contrataciones_data` (un array de ids en vez de
 * objetos): ahí no hay certificado que ligar, así que esas entradas se ignoran.
 */
export function certificadoPorContratacion(orden) {
  const mapa = {};
  lista(orden?.subscriptions).forEach((s) =>
    lista(s?.contrataciones_data).forEach((c) => {
      if (typeof c === 'string') return;
      if (c?.certificado_id && c?.contratacion_id) mapa[c.certificado_id] = c.contratacion_id;
    })
  );
  return mapa;
}

/**
 * Cuántos certificados le faltan a la orden. `0` si no falta ninguno.
 *
 * Solo cuenta en una orden PAGADA. Una orden pendiente o expirada no tiene
 * certificados porque nunca hubo cobro, y eso no es un hueco: es el estado normal.
 * Es la misma regla de dinero del emisor —sin pago verificado no se emite—, así que
 * marcarlas llenaría la pantalla de alarmas falsas (en dev son 20 de 33 órdenes) y
 * volvería inútil el aviso de las que sí importan.
 *
 * Puede dar negativo conceptualmente —una contratación con titular y carga emite
 * dos certificados—, así que se recorta en 0.
 */
export function certificadosFaltantes(orden) {
  if (String(orden?.estado || '').toLowerCase() !== 'pagado') return 0;
  const faltan = contratacionesDeOrden(orden).length - certificadosDeOrden(orden).length;
  return faltan > 0 ? faltan : 0;
}

/**
 * Arma la cadena completa de una fila de `ordenes_pago` traída con sus embeds.
 * Devuelve siempre la misma forma, con `null` en los eslabones que aún no existen
 * (una orden pendiente no tiene pago, ni suscripción, ni certificados).
 *
 * `contratacion` (la primaria) se mantiene para quien ya la usa; `contrataciones`
 * es la lista completa del carrito.
 */
export function trazabilidadDeOrden(orden) {
  const pagos = lista(orden?.pagos).slice().sort(masReciente);
  const suscripciones = lista(orden?.subscriptions).slice().sort(masReciente);

  return {
    contratacion: orden?.contrataciones || null,
    contrataciones: contratacionesDeOrden(orden),
    pago: pagoPrincipal(pagos),
    pagos,
    suscripcion: suscripcionPrincipal(suscripciones),
    suscripciones,
    certificados: certificadosDeOrden(orden),
    certificadoDe: certificadoPorContratacion(orden),
    faltanCertificados: certificadosFaltantes(orden),
  };
}

/**
 * Todos los códigos de la orden en un solo string. La búsqueda de `YoolTable`
 * compara campos de primer nivel con `String(fila[clave])`, así que un objeto
 * anidado nunca calzaría; esto le da una columna plana donde buscar.
 */
export function textoBusquedaOrden(orden) {
  const t = trazabilidadDeOrden(orden);
  return [
    orden?.codigo,
    // Los códigos de TODAS las contrataciones del carrito: buscar CT-2607-1487
    // debe encontrar su orden aunque la primaria sea CT-2607-1486. El nombre, mail
    // y rut son del titular, que es el mismo para todo el carrito.
    ...t.contrataciones.map((c) => c?.codigo),
    t.contratacion?.nombre,
    t.contratacion?.mail,
    t.contratacion?.rut,
    ...t.pagos.map((p) => p?.codigo),
    ...t.suscripciones.flatMap((s) => [s?.codigo, s?.subscription_id]),
    ...t.certificados.map((c) => c?.codigo),
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Ruta al detalle de cada eslabón. Ninguna de esas pantallas tiene ruta por id:
 * son listas que se abren filtradas por el código y ahí se despliega el detalle.
 */
export const rutaContratacion = (codigo) => `/conversiones/contrataciones?search=${encodeURIComponent(codigo)}`;
export const rutaSuscripcion = (codigo) => `/conversiones/suscripciones?search=${encodeURIComponent(codigo)}`;
export const rutaCertificado = (codigo) => `/certificates?search=${encodeURIComponent(codigo)}`;
